<script setup lang="ts">
import { InlineValidationErrorProps } from '@/components/forms/InlineValidationError/types.ts';
import { hasVeeValidationErrorsForPaths } from '@/components/forms/utils.ts';
import { useField, useFormErrors } from 'vee-validate';
import { computed, toRef } from 'vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';

const {
  name,
  type = 'error',
  variant = 'default',
  ignoreTouched = false,
  validateOnMount = false,
  customChildErrorMessage,
} = defineProps<InlineValidationErrorProps>();

const formErrors = useFormErrors();

const hasChildErrors = computed(() => {
  return hasVeeValidationErrorsForPaths(formErrors.value, [name]);
});

const { meta, errorMessage: fieldError } = useField(
  toRef(() => name),
  undefined,
  { validateOnMount },
);

const touched = computed(() => meta.touched);

const errorMessage = computed(() => customChildErrorMessage ?? fieldError.value);

defineOptions({
  inheritAttrs: true,
});
</script>

<template>
  <!-- Icon-only variant with tooltip -->
  <Tooltip v-if="variant === 'icon-only' && errorMessage">
    <template #tooltip>
      {{ errorMessage }}
    </template>

    <template #target="{ props: tooltipProps }">
      <v-alert
        v-bind="tooltipProps"
        :type="type"
        variant="tonal"
        class="validation-status-icon d-inline-flex"
        density="compact"
      >
        <template #text>
          <!-- No text content, icon only -->
        </template>
      </v-alert>
    </template>
  </Tooltip>

  <!-- Default variant -->
  <v-alert
    v-else-if="
      (touched || ignoreTouched) && ((hasChildErrors && customChildErrorMessage) || fieldError)
    "
    :type="type"
    variant="tonal"
    class="text-body-1 text-center"
  >
    <template #text>
      {{ errorMessage }}
    </template>
  </v-alert>
</template>

<style scoped lang="scss">
.validation-status-icon {
  min-width: 24px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  padding: 0 !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(.v-alert__content) {
    display: none;
  }

  :deep(.v-alert__prepend) {
    align-self: center;
    margin: 0 !important;
  }

  :deep(.v-icon) {
    font-size: 16px;
    opacity: 1;
  }
}
</style>
